/**
 * Componente EstoqueAlertasAlert
 * 
 * Wrapper para o componente de alertas de estoque e validade.
 * Gerencia estados de loading, error e empty de forma consistente.
 * 
 * @module components/Dashboard/AlertsSection/EstoqueAlertasAlert
 */

import React from 'react';
import PropTypes from 'prop-types';
import { FaCapsules } from 'react-icons/fa';
import LoadingIndicator from '../../Common/LoadingIndicator';
import Alert from '../../UI/Alert';
import EstoqueAlertasTable from '../../Charts/EstoqueAlertasTable';

/**
 * Componente de alerta de estoque e validade
 * 
 * Renderiza uma seção de alertas críticos de farmácia com título,
 * gerenciamento de estados e tabela de dados.
 * 
 * @param {object} props - Propriedades do componente
 * @param {Array} props.alertas - Dados dos alertas de estoque
 * @param {boolean} props.isLoading - Indica se está carregando
 * @param {Error|string|null} props.error - Erro ocorrido
 * @param {string} [props.title] - Título personalizado
 * @param {string} [props.className] - Classes CSS adicionais
 * @param {string} [props.testId] - ID para testes
 * 
 * @returns {JSX.Element} Componente de alerta
 * 
 * @example
 * ```jsx
 * <EstoqueAlertasAlert
 *     alertas={estoqueAlertasData}
 *     isLoading={isLoading}
 *     error={error}
 * />
 * ```
 */
const EstoqueAlertasAlert = ({
    alertas = null,
    isLoading,
    error = null,
    title = null,
    className = '',
    testId = null,
}) => {
    const defaultTitle = (
        <span className="text-red-700">
            <FaCapsules className="inline mr-2" />
            Alertas Críticos de Farmácia
        </span>
    );
    const displayTitle = title || defaultTitle;

    return (
        <article 
            className={className} 
            data-testid={testId || 'estoque-alertas-alert'}
            role="article"
            aria-labelledby={`${testId || 'estoque-alertas-alert'}-title`}
        >
            <h3 
                id={`${testId || 'estoque-alertas-alert'}-title`}
                className="text-lg font-semibold mb-4"
            >
                {displayTitle}
            </h3>
            <div className="bg-white rounded-lg shadow-md hover:shadow-xl transition-all duration-300 border border-gray-200 hover:-translate-y-1 p-4 relative z-10 hover:z-20" role="region" aria-label="Tabela de alertas de estoque">
                {isLoading ? (
                    <LoadingIndicator 
                        size="small" 
                        message="Buscando alertas de estoque..." 
                    />
                ) : error ? (
                    <Alert 
                        type="error" 
                        message={
                            error?.message 
                                ? `Erro ao buscar alertas de estoque: ${error.message}` 
                                : `Erro ao buscar alertas de estoque: ${error || 'Erro desconhecido'}`
                        } 
                    />
                ) : (
                    <EstoqueAlertasTable alertas={alertas} />
                )}
            </div>
        </article>
    );
};

EstoqueAlertasAlert.propTypes = {
    /** Dados dos alertas de estoque */
    alertas: PropTypes.array,
    
    /** Indica se está carregando */
    isLoading: PropTypes.bool.isRequired,
    
    /** Erro ocorrido (null se não houver) */
    error: PropTypes.oneOfType([
        PropTypes.instanceOf(Error),
        PropTypes.string,
        PropTypes.oneOf([null]),
    ]),
    
    /** Título personalizado (pode ser string ou ReactNode) */
    title: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.node,
    ]),
    
    /** Classes CSS adicionais */
    className: PropTypes.string,
    
    /** ID para testes */
    testId: PropTypes.string,
};

// Default props removidos - usando default parameters na função

export default EstoqueAlertasAlert;

